type DragScrollState = {
  isDragging: boolean;
  startX: number;
  scrollLeft: number;
  diffSumX: number;
};

const INTERACTIVE_ELEMENTS = "a, button, input, select, textarea, [role='button']";

/**
 * Initializes horizontal scroll containers to scroll to the current/active item on page load.
 * Adds drag-to-scroll functionality.
 * Respects user's prefers-reduced-motion setting for accessibility.
 */
export default class SideScrollInit {
  private containers: Map<HTMLElement, DragScrollState>;
  private readonly DRAG_THRESHOLD = 10;

  constructor() {
    this.containers = new Map();
    this.init();
  }

  init(): void {
    const scrollContainers = document.querySelectorAll<HTMLElement>("[data-side-scroll]");
    scrollContainers.forEach((container) => {
      const activeElement = container.querySelector<HTMLElement>("[data-side-scroll-active]");

      if (activeElement) {
        this.scrollToActiveElement(container, activeElement);
      }

      this.initDragScroll(container);
    });
  }

  scrollToActiveElement(container: HTMLElement, element: HTMLElement): void {
    const containerWidth = container.offsetWidth;
    const scrollPosition = element.offsetLeft - containerWidth / 2 + element.offsetWidth / 2;

    const prefersReducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion)").matches;

    if (prefersReducedMotion) {
      container.scrollLeft = scrollPosition;
    } else {
      container.scrollTo({
        left: scrollPosition,
      });
    }
  }

  private initDragScroll(container: HTMLElement): void {
    if (this.isTouchDevice()) {
      return;
    }

    if (container.scrollWidth <= container.clientWidth) {
      return;
    }

    this.containers.set(container, {
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
      diffSumX: 0,
    });

    container.classList.add("side-scroll__content--draggable");
    container.addEventListener("mousedown", (e) => this.handleMouseDown(e, container));
  }

  private handleMouseDown(e: MouseEvent, container: HTMLElement): void {
    // Only handle left-click
    if (e.button !== 0) return;

    // Don't start drag if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest(INTERACTIVE_ELEMENTS)) return;

    const state = this.containers.get(container);
    if (!state) return;

    state.isDragging = true;
    state.startX = e.pageX;
    state.scrollLeft = container.scrollLeft;
    state.diffSumX = 0;

    // Disable smooth scrolling during drag for instant response
    container.style.scrollBehavior = "auto";

    container.classList.add("side-scroll__content--dragging");
    document.body.style.cursor = "grabbing";

    const handleMouseMove = (e: MouseEvent) => this.handleMouseMove(e, container);
    const handleMouseUp = (e: MouseEvent) => {
      this.handleMouseUp(e, container);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    // Prevent default to avoid text selection
    e.preventDefault();
  }

  private handleMouseMove(e: MouseEvent, container: HTMLElement): void {
    const state = this.containers.get(container);
    if (!state || !state.isDragging) return;

    const diffX = e.pageX - state.startX;
    state.diffSumX = diffX;

    container.scrollLeft = state.scrollLeft - diffX;

    if (Math.abs(diffX) > this.DRAG_THRESHOLD) {
      container.style.pointerEvents = "none";
    }
  }

  private handleMouseUp(e: MouseEvent, container: HTMLElement): void {
    const state = this.containers.get(container);
    if (!state) return;

    state.isDragging = false;
    container.classList.remove("side-scroll__content--dragging");
    document.body.style.cursor = "";

    const dragDistance = Math.abs(state.diffSumX);
    if (dragDistance > this.DRAG_THRESHOLD) {
      e.preventDefault();

      // Prevent clicks on child elements during drag
      const preventClick = (clickEvent: Event) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        container.removeEventListener("click", preventClick, true);
      };
      container.addEventListener("click", preventClick, true);
    }

    // Restore pointer events and smooth scrolling
    container.style.pointerEvents = "";
    container.style.scrollBehavior = "";
  }

  private isTouchDevice(): boolean {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }
}

new SideScrollInit();
